import { maybeEmailString, type Organization } from '@local/common-lib';
import { ArrowBack as CancelIcon, Save as SaveIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { CardActions, CardContent, Grid, Typography } from '@mui/material';
import type { FC } from 'react';
import type { Contract } from 'validata';
import { isString, StringFormat } from 'validata';
import { FormikTextField } from '../../components/formik-fields/text-field.js';
import { CenterCard } from '../../components/page-layout/center-card.js';
import type { EditViewProps } from '../../utils/types.js';

export type Type = Pick<
  Organization.Type,
  | 'code'
  | 'name'
  | 'policeStation'
  | 'emailFrom'
  | 'emailSignOff'
  | 'patrollerInstructions'
  | 'patrolMeetUpInstructions'
  | 'eccReplyCcEmail'
> &
  Pick<Organization.Admin, 'email' | 'firstName' | 'lastName'>;

export const typeContract: Contract<Type> = {
  code: isString({
    maxLength: 5,
    minLength: 4,
    regex: /^[A-Z0-9]{4,5}$/,
    transform: (code) => code.toUpperCase(),
  }),
  eccReplyCcEmail: maybeEmailString({ allowDisplayName: true }),
  email: isString({ format: StringFormat.email() }),
  emailFrom: isString({
    format: StringFormat.email({ allowDisplayName: true }),
  }),
  emailSignOff: isString(),
  firstName: isString({ minLength: 2, trim: 'both' }),
  lastName: isString({ minLength: 2, trim: 'both' }),
  name: isString({ minLength: 3, trim: 'both' }),
  patrollerInstructions: isString(),
  patrolMeetUpInstructions: isString(),
  policeStation: isString({ minLength: 3, trim: 'both' }),
};

export const CreateView: FC<EditViewProps<Type>> = ({ cancel, formik }) => {
  return (
    <form onReset={formik.handleReset} onSubmit={formik.handleSubmit}>
      <CenterCard>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <FormikTextField
                formik={formik}
                helperText="Police call sign"
                id="code"
                label="Organization Code"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormikTextField
                formik={formik}
                id="name"
                label="Organization Name"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormikTextField
                formik={formik}
                id="policeStation"
                label="Police Station"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h5">Administrator</Typography>
            </Grid>
            <Grid item xs={6}>
              <FormikTextField
                formik={formik}
                id="firstName"
                label="First Name"
                required
              />
            </Grid>
            <Grid item xs={6}>
              <FormikTextField
                formik={formik}
                id="lastName"
                label="Last Name"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormikTextField
                formik={formik}
                helperText="This will be used for the administrator to sign in"
                id="email"
                label="Email"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h5">Email Configuration</Typography>
            </Grid>
            <Grid item xs={12}>
              <FormikTextField
                formik={formik}
                helperText="The email address that emails are sent from."
                id="emailFrom"
                label="Email From"
                placeholder="Name <email@address.com>"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormikTextField
                formik={formik}
                helperText="Optional email address to CC when Police ECC replies to patrol messages."
                id="eccReplyCcEmail"
                label="CC for Police ECC Replies"
                placeholder="Name <email@address.com>"
              />
            </Grid>
            <Grid item xs={12}>
              <FormikTextField
                formik={formik}
                helperText="Instructions for where and how to meet up for the patrol. These will be sent to patrollers and guests."
                id="patrolMeetUpInstructions"
                label="Patrol Meetup Instructions"
                minRows={4.5}
                multiline
              />
            </Grid>
            <Grid item xs={12}>
              <FormikTextField
                formik={formik}
                helperText="Instructions and reminders for the patrol. These will be sent to patrollers only."
                id="patrollerInstructions"
                label="Patroller Instructions"
                minRows={4.5}
                multiline
              />
            </Grid>
            <Grid item xs={12}>
              <FormikTextField
                formik={formik}
                id="emailSignOff"
                label="Email Sign Off"
                minRows={2.5}
                multiline
              />
            </Grid>
          </Grid>
        </CardContent>
        <CardActions sx={{ justifyContent: 'space-between', pb: 3 }}>
          <LoadingButton
            color="primary"
            loading={formik.isSubmitting}
            onClick={cancel}
            startIcon={<CancelIcon />}
          >
            Cancel
          </LoadingButton>
          <LoadingButton
            color="primary"
            loading={formik.isSubmitting}
            startIcon={<SaveIcon />}
            type="submit"
            variant="contained"
          >
            Save
          </LoadingButton>
        </CardActions>
      </CenterCard>
    </form>
  );
};
